// The single place that talks to the core.
//
// Authentication is a cookie, so nothing here carries a token. What it does
// carry is the CSRF token, which the server sets as a readable cookie and
// expects echoed back on anything that changes state.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly fields?: Record<string, string>,
    readonly body?: unknown,
    /** The machine-readable name of the few refusals we have to act on. */
    readonly code?: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }

  /** True when the session has gone and the user should sign in again. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /**
   * True when the session is real but still owes its second factor.
   *
   * It is not a lost session — throwing the cookie away here would send the
   * person back to the password screen they just came from — so it is asked
   * separately everywhere `isUnauthenticated` is asked.
   */
  get isMfaRequired() {
    return this.code === "mfa_required";
  }

  /**
   * True when the role may not enter this part of the administration.
   *
   * Die Oberfläche blendet diese Bereiche aus, also heißt diese Absage: das,
   * was wir über uns selbst wissen, ist veraltet — ein Tab, der noch offen
   * war, als die Rolle wechselte. Wir holen uns dann unseren Zustand neu,
   * statt die Absage stehen zu lassen.
   */
  get isAreaForbidden() {
    return this.code === "area_forbidden";
  }

  /**
   * True when this installation offers no passkeys at all: no origin is
   * configured on the server. The sign-in page hides its button on this.
   */
  get isPasskeysUnavailable() {
    return this.code === "passkeys_unavailable";
  }
}

let unauthenticated: ((path: string) => void) | undefined;

/**
 * onUnauthenticated registers what happens when any request comes back 401 —
 * the session is gone. It sees every request, including the ones a page makes
 * outside the query cache.
 */
export function onUnauthenticated(fn: (path: string) => void) {
  unauthenticated = fn;
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)mbm_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

type Options = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};

  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && method !== "HEAD") headers["X-CSRF-Token"] = csrfToken();

  const res = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? safeParse(text) : undefined;

  if (!res.ok) {
    if (res.status === 401) unauthenticated?.(path);
    const problem = parsed as
      | { detail?: string; fields?: Record<string, string>; code?: string }
      | undefined;
    throw new ApiError(
      res.status,
      problem?.detail ?? res.statusText,
      problem?.fields,
      parsed,
      problem?.code,
    );
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// --- what the endpoints return -------------------------------------------

export type Page<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * Ein Bereich der Verwaltung, der dem Betreiber gehört und nicht einer Firma:
 * der Roboter, die systemweiten Definitionen, die Abrechnung, die
 * Synchronisation, die Meldungen, das System.
 *
 * Welche davon offenstehen, sagt der Server in `Me.areas`; die Oberfläche
 * denkt sich die Regel nicht selbst aus, damit Menü und Absage dasselbe sagen.
 */
export type Area =
  | "definitions"
  | "robot"
  | "billing"
  | "sync"
  | "alerts"
  | "system";

export type Me = {
  userId: number;
  email: string;
  name: string;
  role: "global_admin" | "self_admin" | "company_admin";
  companyId: number;
  scopeCompanyId: number;
  companies: number[];
  /** The password was right and the second factor is still outstanding. */
  mfaPending: boolean;
  /** Whether there is a second factor to give: enrolment or challenge. */
  mfaEnrolled: boolean;
  /** Die eingeschränkten Bereiche, die diese Rolle betreten darf. */
  areas: Area[];
};

// --- second factor -------------------------------------------------------

export type MfaStatus = {
  required: boolean;
  enrolled: boolean;
  pending: boolean;
  method: string;
  recoveryCodesLeft: number;
  lockedUntil?: string;
  /** Wie viele Passkeys registriert sind. */
  passkeys: number;
  /** Ob diese Installation Passkeys überhaupt anbietet. */
  passkeysAvailable: boolean;
};

// --- passkeys -------------------------------------------------------------

export type Passkey = {
  id: number;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
  aaguid: string;
  transports: string[];
  /** "platform" (das Gerät selbst) oder "cross-platform" (ein Sicherheitsschlüssel). */
  attachment: string;
  backupEligible: boolean;
  backupState: boolean;
};

/**
 * Der Anfang einer Zeremonie: das Token, das sie benennt, und die Optionen
 * für `navigator.credentials`, so wie der Server sie erzeugt hat.
 */
export type PasskeyChallenge<T> = { token: string; options: T };

/**
 * The start of an enrolment. The otpauth:// URI is deliberately absent: the
 * picture is fetched from `qrUrl`, so the shared secret reaches the screen
 * without passing through any script that could keep it.
 */
export type MfaEnrolment = { secret: string; qrUrl: string };

export type MfaRecoveryCodes = { codes: string[] };

export type Company = {
  id: number;
  name: string;
  domain: string;
  billingAccount?: string;
  notifyChannel: "none" | "sms" | "email";
  proxySource: "users" | "secretariats";
};

export type MailboxGreeting = "unconfigured" | "default" | "vacation";
export type BanCheck = "no_number" | "ok" | "failed";
export type RecordingState = "missing" | "own" | "generic";

/**
 * The one-off link that signs a browser in to the portal as somebody else.
 *
 * It is absolute and points at the portal's own hostname, because that is the
 * only origin on which the cookie it sets can land.
 */
export type Impersonation = { url: string; expiresAt: string };

export type User = {
  id: number;
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  gender?: "male" | "female";
  role: string;
  active: boolean;
  msisdn?: string;
  vpn?: string;
  engine: "a1" | "robot" | "hybrid";
  departmentId?: number;
  positionId?: number;

  // The state the overview shows beside each name. A deployment without the
  // master data service serves the plain record, so these are optional.
  mailbox?: MailboxGreeting;
  absenceName?: string;
  absenceUntil?: string | null;
  lastError?: string;
  lastErrorAt?: string | null;
  banCheck?: BanCheck;
  banError?: string;
  recording?: RecordingState;
  proxyRecording?: boolean;
};

export type AnnouncementState =
  | "planned"
  | "activating"
  | "active"
  | "deactivating"
  | "done"
  | "cancelled"
  | "failed";

export type Announcement = {
  id: number;
  userId: number;
  companyMessageId: number;
  proxyUserId?: number;
  startsAt: string;
  endsAt: string | null;
  state: AnnouncementState;
  notify: "none" | "self" | "self_and_proxy";
  repeat: string;
  displayName?: string;
  errorStage: number;
};

export type AvailableMessage = {
  id: number;
  name: string;
  body: string;
  proxyRequired: boolean;
  dateMode: "end_required" | "end_optional" | "minutes_only" | "end_with_time";
  notifyAllowed: boolean;
};

export type Job = {
  id: number;
  queue: string;
  kind: string;
  state: string;
  attempt: number;
  maxAttempts: number;
  runAt: string;
  lastError?: string;
  /** The person the job is about; absent for company-wide work such as billing. */
  userId?: number;
  userName?: string;
  createdAt: string;
  finishedAt?: string;
};

/** A conflict response names the absences that are in the way. */
export type ConflictBody = {
  detail: string;
  conflicts?: Announcement[];
};

// --- master data ---------------------------------------------------------

/** The whole of a company's configuration, as the detail page edits it. */
export type CompanyDetail = {
  id: number;
  name: string;
  domain: string;
  connectorUrl: string;
  billingAccount: string;

  logoKey: string;
  backgroundKey: string;
  logoMobileKey: string;
  backgroundMobileKey: string;
  colorMobile: string;
  backgroundPosition: string;

  notifyChannel: "none" | "sms" | "email";
  textActivateWithProxy: string;
  textActivateWithoutProxy: string;
  textActivateProxy: string;
  textDeactivate: string;
  textDeactivateProxy: string;
  emailFooter: string;

  welcomeMailSubject: string;
  welcomeMailBody: string;
  welcomeMailSender: string;
  welcomeMailTrigger: string;
  mailTransport: string;

  proxySource: "users" | "secretariats";
  defaultMfaMode: string;
  passwordPolicyRegex: string;
  passwordPolicyHint: string;
  defaultAnnouncement: string;

  billingEnabled: boolean;
  pricePerUser: number;
  priceCompanyBase: number;
  billingTextUser: string;
  billingTextCompany: string;
  minimumUsers: number;

  syncEnabled: boolean;
  active: boolean;

  // Kalender-Ansagen: aus, bis jemand sie einschaltet. Ohne calendarMessageId
  // entsteht aus einer Einladung nichts — das Altsystem nahm hier die
  // Standardansage, also die Ansage für "niemand ist abwesend".
  calendarSyncEnabled: boolean;
  calendarMessageId: number;
  calendarNotify: string;

  userCount?: number;
  departmentCount?: number;
};

export type Unit = {
  id: number;
  companyId: number;
  name: string;
  userCount: number;
  messageIds?: number[];
};

/** A person as the detail page edits them. The PIN is write-only. */
export type UserDetail = {
  id: number;
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  gender: string;
  personnelNo: string;
  role: string;
  mfa: string;
  passkeys: number;
  active: boolean;
  demo: boolean;
  msisdn: string;
  spokenName: string;
  vpnNumber: string;
  billingAccount: string;
  deliveryEngine: "a1" | "robot" | "hybrid";
  dialerScriptId: number;
  /** The hybrid robot's plan for this mailbox; required when deliveryEngine is "hybrid". */
  hybridProvider: string;
  departmentId: number;
  departmentIds: number[];
  positionId: number;
  /** The legacy defaultProxy and defaultVirtualProxy: a colleague and an office. */
  favouriteProxyId: number;
  favouriteSecretariatId: number;
  billable: boolean;
  banCheck?: string;
  banError?: string;
  recording?: string;
  hasPin: boolean;
  password?: string;
  mailboxPin?: string;
};

// --- announcement definitions --------------------------------------------

export type Block = {
  id: number;
  namePattern: string;
  description: string;
  variable: string;
  usedBy: number;
};

export type Step = { blockId: number; param?: number; pattern?: string };

export type Template = {
  id: number;
  name: string;
  description: string;
  steps: Step[];
  legacyBlocks?: string;
  proxyRequired: boolean;
  callType: "record" | "no_record";
  dateMode: string;
  proxySource: string;
  notifyAllowed: boolean;
  fallbackSuffix: string;
  usedBy: number;
};

export type CompanyMessage = {
  id: number;
  companyId: number;
  templateId: number;
  name: string;
  subtitle: string;
  body: string;
  backgroundFadeAt: number;
  isDefault: boolean;
  notifyAllowed: boolean;
  notifyDefault: string;
  notifyOverridable: boolean;
  endLabelInclusive: boolean;
  active: boolean;
  templateName?: string;
  dateMode?: string;
  inUse?: number;
};

export type DialerScript = {
  id: number;
  name: string;
  body: string;
  usedBy: number;
};

// --- recordings ----------------------------------------------------------

export type AudioEntry = {
  name: string;
  present: boolean;
  key?: string;
  size?: number;
  modTime?: string;
};

export type AudioGroup = {
  blockId: number;
  pattern: string;
  description: string;
  variable: string;
  expected: number;
  missing: number;
  entries: AudioEntry[];
};

export type AudioInventory = {
  companyId: number;
  expected: number;
  missing: number;
  present: number;
  groups: AudioGroup[];
  orphans: AudioEntry[];
  truncated: boolean;
};

export type PreviewResult = {
  url: string;
  durationSeconds: number;
  segments: { blockId: number; pattern: string; resolved: string; reason: string }[];
  missing: { blockId: number; pattern: string; reason: string }[];
};

// --- operations ----------------------------------------------------------

export type Dashboard = {
  announcementsActive: number;
  announcementsPlanned: number;
  announcementsFailed: number;
  jobsPending: number;
  jobsRunning: number;
  jobsFailed: number;
  jobsDead: number;
  outboxPending: number;
  outboxFailed: number;
  alertsOpen: number;
  companies: number;
  users: number;
  billingMonth: string;
  billing: { process: string; state: string; step: string }[];
  recent: {
    userId: number;
    name: string;
    company: string;
    kind: string;
    at: string;
  }[];
};

export type Alert = {
  id: number;
  level: "info" | "warning" | "critical";
  source: string;
  subject: string;
  body: string;
  companyId: number | null;
  occurrences: number;
  firstSeen: string;
  lastSeen: string;
  ackedAt: string | null;
};

export type MonthCount = { month: string; count: number };
export type Bucket = { label: string; users: number };
export type ChannelUse = { channel: string; count: number };
export type CompanyActivations = {
  companyId: number;
  name: string;
  count: number;
  users: number;
};

/** A number and the same number for the window before; null where there is
 *  nothing honest to compare with. */
export type Delta = { value: number; previous: number | null };
export type Grain = "day" | "week" | "month";
export type Timeline = {
  grain: Grain;
  buckets: string[];
  series: { key: string; values: number[] }[];
};
export type Slice = { key: string; value: number };

export type UsageReport = {
  kpi: Record<"activations" | "people" | "perPerson" | "adoption" | "entered", Delta>;
  greetings: Timeline;
  sources: Timeline;
  heatmap: { weekday: number; hour: number; count: number }[];
  channels: Slice[];
  repeats: Slice[];
  durations: Slice[];
  leadTimes: Slice[];
  distribution: Bucket[];
  topPeople: { userId: number; name: string; company: string; count: number }[];
};

export type PeopleReport = {
  kpi: Record<"companies" | "users" | "newUsers" | "billable" | "idle", Delta>;
  growth: Timeline;
  engines: Slice[];
  roles: Slice[];
  logins: Slice[];
  companies: {
    companyId: number;
    name: string;
    active: boolean;
    users: number;
    activations: number;
    people: number;
    previous: number;
    trend: number[];
  }[];
};

export type QualityReport = {
  kpi: Record<"successRate" | "failed" | "userErrors" | "jobFailRate", Delta>;
  outcomes: Timeline;
  errors: Timeline;
  providerCodes: Slice[];
  announcementCodes: Slice[];
  jobs: { kind: string; runs: number; failed: number; avgSeconds: number }[];
  robot?: {
    calls: Delta;
    successRate: Delta;
    timeline: Timeline;
    p50Seconds: number;
    p90Seconds: number;
    hybridOutcomes: Slice[];
    hybridModes: {
      provider: string;
      mode: string;
      calls: number;
      withFallback: number;
      succeeded: number;
      avgLlmCalls: number;
      avgHandoffs: number;
      avgSeconds: number;
    }[];
    reviews: Slice[];
  };
};

export type RevenueReport = {
  kpi: Record<"total" | "perCompany" | "users" | "minimumShare", Delta>;
  months: Timeline;
  perUser: Timeline;
  companies: { companyId: number; name: string; amount: number; previous: number }[];
};

export type AuditEntry = {
  id: number;
  actorId: number;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: number;
  companyId: number;
  company: string;
  before?: unknown;
  after?: unknown;
  ip: string;
  at: string;
};

/** One file in the log directory, as the server lists it. */
export type LogFile = {
  name: string;
  area: string;
  size: number;
  modifiedAt: string;
  rotated: boolean;
};

/** One line of a log file; the columns are empty when `parsed` is false. */
export type LogLine = {
  n: number;
  parsed: boolean;
  raw: string;
  level: string;
  at: string;
  exec: number;
  pid: string;
  session: string;
  correlation: string;
  userId: string;
  op: string;
  message: string;
  attrs: { key: string; value: string }[];
};

export type UserErrorEntry = {
  id: number;
  userId: number;
  userName: string;
  companyId: number;
  company: string;
  kind: string;
  providerCode: string;
  message: string;
  detail: string;
  correlationId: string;
  at: string;
};

export type EventEntry = {
  id: number;
  userId: number;
  userName: string;
  companyId: number;
  kind: string;
  note: string;
  at: string;
};

export type JobRun = {
  id: number;
  jobId: number;
  attempt: number;
  workerId: string;
  startedAt: string;
  finishedAt: string | null;
  outcome: string;
  errorCode: string;
  error: string;
  log: string;
};

// --- billing -------------------------------------------------------------

export type BillingRun = {
  id: number;
  month: string;
  process: string;
  state: "pending" | "success" | "escalated";
  step: string;
  attempts: number;
  maxAttempts: number;
  lastAttemptAt: string | null;
  lastError: string;
  uploadedAs: string;
  notes: string;
  records: number;
  amount: number;
};

export type BillingSummaryRow = {
  companyId: number;
  company: string;
  users: number;
  userTotal: number;
  baseFee: number;
  minimum: number;
  manual: number;
  total: number;
};

export type ManualItem = {
  id: number;
  companyId: number;
  company?: string;
  billingAccount: string;
  month: string;
  description: string;
  amount: number;
  billedRunId?: number;
  billedAt: string | null;
};

export type BillingFile = {
  id: number;
  key: string;
  filename: string;
  origin: string;
  month: string;
  process: string;
  size: number;
  sha256: string;
  at: string;
};

// --- synchronisation -----------------------------------------------------

export type OutboxEntry = {
  ID: number;
  Target: string;
  Aggregate: string;
  AggregateID: number;
  Kind: string;
  Attempts: number;
};

// --- robot console -------------------------------------------------------

export type SampleFile = {
  path: string;
  dir: string;
  name: string;
  category: string;
  llmCategory?: string;
  llmConfidence?: number;
  transcript?: string;
  durationMs: number;
  rms: number;
  peak: number;
  clip: number;
  rate: number;
  channels: number;
  digit?: string;
  digitByName?: string;
  referencedBy?: string[];
  duplicateOf?: string;
  findings: string[];
  error?: string;
};

export type SampleReport = {
  id: number;
  createdAt: string;
  workDir: string;
  totals: Record<string, number>;
  report: {
    generated: string;
    files: SampleFile[];
    missing: { sample: string; scripts: string[] }[];
    notes?: string[];
  };
};

export type ExploreAction = {
  kind: "dial" | "keys" | "beep" | "greeting" | "wait";
  keys: string;
  hz?: number;
  waitMs?: number;
  note?: string;
};

export type ExploreNode = {
  index: number;
  kind: string;
  depth: number;
  session: string;
  exitCode: number;
  message?: string;
  transcript: string;
  features?: { durationMs: number; beepHz: number; promptHz?: number; silent: boolean; hitCap: boolean };
  pruned?: boolean;
  listen?: string;
  decision?: {
    key: string;
    intent: string;
    done: boolean;
    needs_greeting: boolean;
    expects_beep: boolean;
    confidence: number;
    reason: string;
    source?: string;
  };
  loop?: boolean;
  blocked?: string[];
  category?: string;
};

export type Exploration = {
  id: number;
  createdAt: string;
  startedBy: number;
  companyId: number;
  provider: string;
  msisdn: string;
  audioKey: string;
  maxCalls: number;
  probes: string;
  state: "queued" | "running" | "done" | "failed" | "cancelled";
  live: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  status: string;
  reason: string;
  calls: number;
  path: ExploreAction[];
  nodes: ExploreNode[];
  samples: { path: string; category: string; transcript: string }[];
  script: string;
  scriptId: number;
  verify: string;
  log: string;
  cancelRequested: boolean;
};

export type RobotReview = {
  id: number;
  createdAt: string;
  kind: "transcript" | "listen";
  sessionId: string;
  callId: number;
  announcementId: number;
  userId: number;
  userName: string;
  companyId: number;
  msisdn: string;
  state: "queued" | "running" | "done" | "failed";
  verdict: string;
  summary: string;
  transcripts: { file: string; seconds: number; text: string; category: string }[];
  error: string;
  finishedAt: string | null;
};

export type RobotCall = {
  id: number;
  sessionId: string;
  userId: number;
  userName: string;
  companyId: number;
  company: string;
  msisdn: string;
  scriptId: number;
  scriptName: string;
  audioKey: string;
  note: string;
  state: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  exitCode: number | null;
  error: string;
  live: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number;
  startedBy: number;
  createdAt: string;
};

// --- hybrid robot ----------------------------------------------------------

export type HybridProvider = {
  key: string;
  displayName: string;
  /** Number of the mailbox centre; empty means direct dial with dialIn. */
  serviceNumber: string;
  /** Network prefix → digits inserted after it (direct dial), e.g. {"0677":"60"}. */
  dialIn: Record<string, string>;
  /** The PIN is keyed without a trailing #. */
  pinRaw: boolean;
  /** A wrong PIN may lock the box. */
  lockoutRisk: boolean;
  /** The box keeps a recording once # is pressed and confirms nothing (Hot). */
  implicitSave: boolean;
  activeVersion: number;
  versions: number;
};

export type HybridProviderInput = Omit<HybridProvider, "activeVersion" | "versions">;

export type HybridPlanSource = "seed" | "manual" | "learned";

export type HybridProvenance = {
  learned_from?: string;
  at?: string;
  by?: string;
  confidence?: number;
  reason?: string;
  sightings?: number;
  confirmations?: number;
  failures?: number;
  uses?: number;
  last_used?: string;
  candidate?: boolean;
  disabled?: boolean;
};

export type HybridPlanAction = {
  keys?: string;
  wait_beep?: { hz?: number[]; timeout_ms?: number; optional?: boolean };
  settle?: { level?: number; hold_ms?: number; timeout_ms?: number };
  play_greeting?: { level?: number };
  wait_ms?: number;
  hangup?: boolean;
};

export type HybridCond = {
  pin_entered?: boolean;
  greeting_played?: boolean;
  save_key_pressed?: boolean;
  min_visits?: number;
  max_visits?: number;
};

export type HybridTransition = {
  when: { snippet?: string; after_ms?: number; if?: HybridCond; else?: boolean };
  do?: HybridPlanAction[];
  next?: string;
  outcome?: string;
  policy?: string;
  immediate?: boolean;
  /** Recording step: heard to its tone, never keyed early. */
  hold?: boolean;
  /** "ok": the box takes the keys while it talks; "no": only after; absent: untested. */
  barge_in?: "ok" | "no";
  /** The option the key chose, in the box's words; announced again under any key, that key is pressed. */
  choice?: string;
  provenance?: HybridProvenance;
};

export type HybridWording = {
  text: string;
  learned_from?: string;
  at?: string;
  by?: string;
  state?: string;
  sightings?: number;
};

export type HybridSnippet = {
  meaning: string;
  /** The prompt as it was first heard when learned: the first wording. */
  description?: string;
  /** The hearings of the prompt; it is recognised when one is heard again nearly whole. */
  wordings?: HybridWording[];
  audio?: string;
  /** How much of the recording's beginning tells the prompt apart; -1 none, absent not measured. */
  head_ms?: number;
  /** The prompt carries content of its own (a playback); its end is heard, never assumed. */
  variable?: boolean;
  provenance?: HybridProvenance;
};

/** One eventuality of the catalogue every provider's playbook shares. */
export type HybridSituation = {
  meaning: string;
  criterion: string;
  reaction: string;
  outcome?: string;
  phases: number;
};

/** One entry of the robot's lexicon: the words a box says a thing with, and who reads them. */
export type HybridTerm = {
  id: string;
  label: string;
  /** The catalogue's situation the words belong to. */
  meaning?: string;
  /** parser | veto | back | end_key | model */
  use: string[];
  /** substring | word_start | phrase */
  match: string;
  de: string[];
  en?: string[];
  note?: string;
};

/** A key as a box (or Whisper) says it. */
export type HybridKeyWord = { word: string; key: string; lang: string; misheard?: boolean; after?: string };

/** A typical course of a mailbox call. */
export type HybridFlow = {
  id: string;
  title: string;
  summary: string;
  signals?: string[];
  steps: { box: string; robot: string; meaning?: string }[];
  /** Where it was heard: real calls or the production scripts. */
  seen?: string;
  handling: string;
  /** The course in one line, as the language model reads it. */
  model: string;
};

export type HybridRule = { id: string; title: string; text: string; where: string };

/** The seed every provider starts with (internal/domain/hybrid/knowledge.go). */
export type HybridKnowledge = {
  terms: HybridTerm[];
  key_words: HybridKeyWord[];
  veto_patterns: string[];
  end_key_fallbacks: string[];
  flows: HybridFlow[];
  rules: HybridRule[];
  situations: HybridSituation[];
  seeds: { provider: string; plan: HybridPlan }[];
  /** What the language model reads of it, after its rules. */
  model_text: string;
};

/** What systemone made of an utterance (step events carry it). */
export type HybridRead = {
  meaning: string;
  catalogue?: string;
  catalogue_sure?: boolean;
  p: number;
  margin: number;
  sure: boolean;
  snippet?: string;
  snippet_p?: number;
  key?: string;
  clause?: string;
  option_p?: number;
  option_sure?: boolean;
  ms: number;
};

export type HybridPlan = {
  schema: number;
  provider: string;
  version?: number;
  updated_at?: string;
  dial: { mode: string; service?: string; dial_in_by_prefix?: Record<string, string> };
  settings: Record<string, unknown>;
  start: string;
  states: Record<
    string,
    { meaning: string; transitions?: HybridTransition[]; timeout_ms?: number; max_visits?: number }
  >;
  global?: HybridTransition[];
  snippets: Record<string, HybridSnippet>;
  history?: { version: number; at?: string; session?: string; changes?: string[] }[];
};

export type HybridPlanVersion = {
  id: number;
  provider: string;
  version: number;
  source: HybridPlanSource;
  learnedFromCallId: number;
  learnedFromSession: string;
  note: string;
  createdBy: number;
  createdAt: string;
  active: boolean;
  plan?: HybridPlan;
};

export type HybridCallState = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export type HybridCall = {
  id: number;
  sessionId: string;
  origin: "console" | "announcement";
  announcementId: number;
  userId: number;
  userName: string;
  companyId: number;
  company: string;
  msisdn: string;
  provider: string;
  planVersion: number;
  /** Always "hybrid" now; older calls hold the modes that existed before. */
  mode: string;
  greetingKey: string;
  note: string;
  state: HybridCallState;
  outcome: string;
  code: number | null;
  error: string;
  steps: number;
  llmCalls: number;
  handoffs: number;
  fallbacks: number;
  /** The call recognised prompts by the snippets' sound first (off: systemone read every prompt). */
  snippets: boolean;
  learnedVersion: number;
  summary: string;
  live: boolean;
  cancelRequested: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number;
  startedBy: number;
  createdAt: string;
};

export type HybridEventKind =
  | "session"
  | "line"
  | "utterance"
  | "tone"
  | "step"
  | "action"
  | "warning"
  | "learned"
  | "error"
  | "done";

export type HybridCallEvent = {
  seq: number;
  atMs: number;
  kind: HybridEventKind;
  payload: Record<string, unknown>;
  createdAt: string;
};

export type HybridCallRequest = {
  userId?: number;
  companyId?: number;
  msisdn: string;
  pin: string;
  provider: string;
  audioKey?: string;
  note?: string;
  /** "on": the snippets' sound first; "off": systemone reads every prompt; "": the global setting. */
  snippets?: "" | "on" | "off";
};

/** The robot's own settings, for every provider and call. */
export type HybridSettings = {
  snippets: boolean;
  updatedAt: string;
  updatedBy: number;
};

/**
 * A hint for qwen about one provider's box (docs/15, "Hinweise für qwen"):
 * a free instruction, or a prompt explained. qwen reads the enabled ones
 * after its general knowledge on every call of the provider.
 */
export type HybridHintKind = "instruction" | "prompt";

export type HybridHint = {
  id: number;
  provider: string;
  kind: HybridHintKind;
  transcript?: string;
  meaning?: string;
  /** keys | wait | wait_beep | play_greeting | done | abort, or empty for none. */
  action?: string;
  keys?: string;
  text?: string;
  enabled: boolean;
  callId?: number;
  session?: string;
  useq?: number;
  createdBy?: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
};

export type HybridHintInput = {
  kind: HybridHintKind;
  transcript: string;
  meaning: string;
  action: string;
  keys: string;
  text: string;
  enabled: boolean;
  callId?: number;
  session?: string;
  useq?: number;
};

export type HybridHintList = {
  hints: HybridHint[];
  /** What qwen reads of them, as the calls get it. */
  rendered: string;
};

/** One prompt of a provider's box as heard across calls. */
export type HybridPromptGroup = {
  key: string;
  text: string;
  variants: { text: string; heard: number }[];
  heard: number;
  calls: number;
  lastAt: string;
  examples: { callId: number; session: string; useq: number; at: string; outcome: string }[];
  byHead?: boolean;
  snippet?: string;
  snippetMeaning?: string;
  variable?: boolean;
  decidedBy: Record<string, number>;
  did: Record<string, number>;
  handoffs: Record<string, number>;
  asked: number;
  refused: number;
  failedCalls: number;
  stuck: boolean;
  hints: number[];
};

export type HybridPromptList = {
  calls: number;
  days: number;
  prompts: HybridPromptGroup[];
};

/** Where the administration plays a snippet's recording (audio: "snippets/<provider>/<file>"). */
export function snippetAudioUrl(audio: string): string | null {
  const m = /^snippets\/([a-z0-9_-]+)\/([a-z0-9_]+-[0-9a-f]{16}\.wav)$/.exec(audio);
  return m ? `/api/media/hybrid/snippets/${m[1]}/${m[2]}` : null;
}

export const hybridApi = {
  settings: () => api<HybridSettings>("/api/admin/hybrid/settings"),
  updateSettings: (body: { snippets: boolean }) =>
    api<HybridSettings>("/api/admin/hybrid/settings", { method: "PUT", body }),
  situations: () => api<HybridSituation[]>("/api/admin/hybrid/situations"),
  knowledge: () => api<HybridKnowledge>("/api/admin/hybrid/knowledge"),
  providers: () => api<HybridProvider[]>("/api/admin/hybrid/providers"),
  createProvider: (body: HybridProviderInput) =>
    api<HybridProvider>("/api/admin/hybrid/providers", { method: "POST", body }),
  updateProvider: (key: string, body: HybridProviderInput) =>
    api<HybridProvider>(`/api/admin/hybrid/providers/${encodeURIComponent(key)}`, { method: "PUT", body }),
  versions: (provider: string) =>
    api<HybridPlanVersion[]>(`/api/admin/hybrid/plans/${encodeURIComponent(provider)}`),
  activePlan: (provider: string) =>
    api<HybridPlanVersion>(`/api/admin/hybrid/plans/${encodeURIComponent(provider)}/active`),
  version: (provider: string, version: number) =>
    api<HybridPlanVersion>(
      `/api/admin/hybrid/plans/${encodeURIComponent(provider)}/versions/${version}`,
    ),
  savePlan: (provider: string, body: { plan: unknown; note: string }) =>
    api<HybridPlanVersion>(`/api/admin/hybrid/plans/${encodeURIComponent(provider)}`, {
      method: "POST",
      body,
    }),
  activate: (provider: string, version: number) =>
    api<void>(
      `/api/admin/hybrid/plans/${encodeURIComponent(provider)}/versions/${version}/activate`,
      { method: "POST" },
    ),
  /** Deactivates every version: the next call learns the box from nothing. */
  forget: (provider: string) =>
    api<{ deactivated: number }>(`/api/admin/hybrid/plans/${encodeURIComponent(provider)}/forget`, {
      method: "POST",
    }),
  validate: (plan: unknown) =>
    api<{ ok: boolean }>("/api/admin/hybrid/plans/validate", { method: "POST", body: { plan } }),
  calls: (limit = 100) => api<HybridCall[]>(`/api/admin/hybrid/calls?limit=${limit}`),
  call: (id: number) => api<HybridCall>(`/api/admin/hybrid/calls/${id}`),
  startCall: (body: HybridCallRequest) =>
    api<HybridCall>("/api/admin/hybrid/calls", { method: "POST", body }),
  cancelCall: (id: number) => api<void>(`/api/admin/hybrid/calls/${id}/cancel`, { method: "POST" }),
  events: (id: number, since: number, limit = 500) =>
    api<HybridCallEvent[]>(`/api/admin/hybrid/calls/${id}/events?since=${since}&limit=${limit}`),
  hints: (provider: string) =>
    api<HybridHintList>(`/api/admin/hybrid/providers/${encodeURIComponent(provider)}/hints`),
  createHint: (provider: string, body: HybridHintInput) =>
    api<HybridHint>(`/api/admin/hybrid/providers/${encodeURIComponent(provider)}/hints`, { method: "POST", body }),
  updateHint: (id: number, body: HybridHintInput) =>
    api<HybridHint>(`/api/admin/hybrid/hints/${id}`, { method: "PUT", body }),
  deleteHint: (id: number) => api<void>(`/api/admin/hybrid/hints/${id}`, { method: "DELETE" }),
  prompts: (provider: string, days = 30) =>
    api<HybridPromptList>(`/api/admin/hybrid/providers/${encodeURIComponent(provider)}/prompts?days=${days}`),
};

// --- speech ---------------------------------------------------------------

export type SpeechReport = {
  engine: string;
  model: string;
  voice?: string;
  cloneKey?: string;
  version?: string;
  text: string;
  cacheKey: string;
  cached: boolean;
  synthesisMs?: number;
  /** The company has a recorded voice that this engine could not use. */
  voiceFellBack?: boolean;
  voiceNote?: string;
};

export type SpeechEngineInfo = {
  configured: boolean;
  name?: string;
  model?: string;
  version?: string;
  supportsClone: boolean;
  maxInputChars?: number;
  /** Why an option is unavailable, in words an administrator can act on. */
  note?: string;
};

export type SpeechSettings = {
  mode: string; // "snippets" | "tts"
  voice: string;
  speed: number;
  maleSalutation: string;
  femaleSalutation: string;
  shortPauseMs: number;
  mediumPauseMs: number;
  cloneKey: string;
  cloneTranscript: string;
  cloneSha256: string;
  cloneBytes: number;
  requireClone: boolean;
  engine: SpeechEngineInfo;
};

export type SpeechBlock = {
  blockId: number;
  namePattern: string;
  description: string;
  variable: string;
  /** The company's own wording; fallback is the shared default. */
  text: string;
  fallback: string;
  source: string; // "asr" | "description" | "manual" | ""
  confirmed: boolean;
  recording?: string;
};

export type TranscribeReport = {
  transcribed: number;
  fromFallback: number;
  skipped: number;
  notes?: string[];
};

// --- how a company's people sign in --------------------------------------

export type AuthMode = "local" | "oidc";

export type AuthConfig = {
  companyId: number;
  mode: AuthMode;
  issuer?: string;
  clientId?: string;
  /** A reference into the secret store (env:… or file:…), never the secret. */
  credentialsRef?: string;
  scopes?: string;
  emailClaims?: string;
  requireVerifiedEmail: boolean;
  allowJitProvisioning: boolean;
  displayName?: string;
  extraParams?: Record<string, string>;
  enabled: boolean;
  /** The mail domains this company has claimed; edited one at a time. */
  domains: string[];
  /** Whether credentialsRef points at something that exists. Absent when there is no reference. */
  secretResolves?: boolean;
  /** Active people without a password: whom a move to local passwords would lock out. */
  passwordlessCount: number;
  /** What the customer registers at their provider. Absent when no portal address is configured. */
  redirectUri?: string;
};
